import { ANNOUNCEMENT_SURFACES, type AnnouncementNarrowInput, type AnnouncementSurface } from '../constants';
import { narrowAnnouncementSettings } from './narrowAnnouncementSettings';
import { parseTimestamp } from './timestamps';

export type ConflictAnnouncement = AnnouncementNarrowInput & {
  expiresAt?: string | null;
  id: string;
  publishedAt?: string | null;
};

export type AnnouncementConflict<T extends ConflictAnnouncement> = {
  holder: T;
  surface: AnnouncementSurface;
};

/**
 * Published and not yet expired. A future `startsAt` still holds the surface.
 * An unpublished draft (`publishedAt` null) does not.
 */
const holdsSurface = (announcement: { expiresAt?: string | null; publishedAt?: string | null }, now: Date): boolean => {
  if (parseTimestamp(announcement.publishedAt) === null) {
    return false;
  }

  if (!announcement.expiresAt) {
    return true;
  }

  const expiresAt = parseTimestamp(announcement.expiresAt);

  return expiresAt !== null && expiresAt > now.getTime();
};

/**
 * For each surface the candidate is placed on, the active announcement that already holds it.
 * Active means published and not expired. Audience and start time do not matter.
 */
export const findAnnouncementConflicts = <T extends ConflictAnnouncement>({
  candidate,
  now,
  others,
}: {
  candidate: T;
  now: Date;
  others: readonly T[];
}): AnnouncementConflict<T>[] => {
  const candidatePlacements = narrowAnnouncementSettings(candidate).placements;
  const conflicts: AnnouncementConflict<T>[] = [];

  for (const surface of ANNOUNCEMENT_SURFACES) {
    if (!candidatePlacements[surface]) {
      continue;
    }

    const holder = others.find((other) => {
      if (other.id === candidate.id || !holdsSurface(other, now)) {
        return false;
      }

      return Boolean(narrowAnnouncementSettings(other).placements[surface]);
    });

    if (holder) {
      conflicts.push({ holder, surface });
    }
  }

  return conflicts;
};
